import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  writeBatch,
  Timestamp,
  type DocumentData,
} from 'firebase/firestore';
import { db as fdb } from './firebase';
import { decryptDoc, encryptDoc } from './crypto/syncCrypto';
import { vaultKey } from './crypto/vault';
import type { RemoteDoc } from './syncDecide';
import type { RemoteSync } from './syncEngine';
import { BATCH_SIZE } from './syncEngine';

/**
 * Firestore behind the sync engine's small interface.
 *
 * Writes go out in batches, so a library arriving on a new device — or a burst
 * of edits — costs one round trip per few hundred documents rather than one per
 * document. `undefined` is not a value Firestore accepts, so fields that are
 * absent are dropped rather than sent.
 *
 * Encryption sits here rather than in the engine: the engine reasons about
 * clocks and conflicts, and none of that changes when the text it is moving is
 * sealed. Going out, a document is encrypted if this account has a vault;
 * coming in, one that cannot be opened is **skipped**, so a device that is
 * locked keeps the notes it already has instead of overwriting them with
 * something unreadable.
 */
export function forFirestore(document: RemoteDoc): DocumentData {
  const clean: DocumentData = { ...document, deletedAt: document.deletedAt ?? null };
  for (const [key, value] of Object.entries(clean)) {
    if (value === undefined) delete clean[key];
  }
  // A TTL policy only looks at timestamp fields, so a tombstone's expiry has to
  // be one — as a number it would sit there for ever instead of being cleaned up.
  if (typeof clean.expiresAt === 'number') clean.expiresAt = Timestamp.fromMillis(clean.expiresAt);
  return clean;
}

/**
 * @param onLocked Called with how many records this device could not open, so
 * the UI can ask for the passphrase instead of silently showing an old library.
 */
export function firestoreRemoteSync(uid: string, onLocked?: (count: number) => void): RemoteSync {
  return {
    watch(onDocs, onError) {
      const q = query(collection(fdb, 'documents'), where('ownerId', '==', uid));
      return onSnapshot(
        q,
        (snapshot) => {
          const changed = snapshot
            .docChanges()
            // A removal is the record itself going away — an old client's
            // delete, or a tombstone past its TTL. Nothing to apply: a real
            // deletion arrives as a tombstone, which is a change like any other.
            .filter((change) => change.type !== 'removed')
            .map((change) => change.doc.data() as RemoteDoc);
          void (async () => {
            const key = vaultKey();
            const opened: RemoteDoc[] = [];
            let locked = 0;
            for (const document of changed) {
              const plain = await decryptDoc(document, key);
              if (plain) opened.push(plain as RemoteDoc);
              else locked++;
            }
            if (locked) onLocked?.(locked);
            // Always delivered, even empty: that is how the engine knows the
            // first round has arrived and it may start pushing. Without it, a
            // new account would wait out the safety timeout before its first
            // document went up.
            onDocs(opened);
          })();
        },
        onError,
      );
    },

    async put(docs) {
      const key = vaultKey();
      for (let i = 0; i < docs.length; i += BATCH_SIZE) {
        const batch = writeBatch(fdb);
        for (const document of docs.slice(i, i + BATCH_SIZE)) {
          const outgoing = key ? await encryptDoc(document, key) : document;
          batch.set(doc(fdb, 'documents', document.id), forFirestore(outgoing));
        }
        await batch.commit();
      }
    },
  };
}
